import {
  Component,
  OnDestroy,
  Input,
  ViewEncapsulation,
  OnInit,
} from "@angular/core";

import { Observable, of, Subscription, zip } from "rxjs";
import { catchError, skip, switchMap } from "rxjs/operators";
import { Store, select } from "@ngrx/store";

import {
  getPropertiesForListByUsage,
  getSiteProductList,
  getSiteWarehouseList,
  getWarehouseTypesList,
} from "src/app/store/actions/sites/sites.actions";
import {
  AppState,
  warehouseTypesLoadedState,
  siteWarehousesLoadedState,
  loadedDeviceWarehousePropertiesListSiteState,
  siteProductListLoadedState,
  loadedTenantState,
} from "src/app/store/reducers";
import {
  SiteWarehouseDTO,
  TankStockDTO,
  WarehouseType,
} from "src/app/models/Sites/siteWarehouseDTO";

import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { WarehouseFormDialogComponent } from "./dialog/warehouse-form-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { TenantDataFormatDTO } from "src/app/models/Tenant/Tenant.model";
import { DashboardService } from "src/app/shared/services/dashboard/dashboard.service";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

const USAGE_CODE = "DEVICE_WAREHOUSE";

@Component({
  selector: "site-warehouse-details",
  templateUrl: "./warehouse-details.component.html",
  styleUrls: ["./warehouse-details.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class WarehouseDetailsComponent implements OnInit, OnDestroy {
  _siteCode: string;
  
  @Input() warehouseType: string;
  @Input() siteNumber: string;
  @Input() set siteCode(value) {
    this._siteCode = value;
    if (this._siteCode) {
      this.loadWarehouseInfo(this._siteCode);
      this.loadSiteWarehouses(this._siteCode);
    }
  }
  @Input() permissionResource: string;
  
  public tenantDataFormat!: TenantDataFormatDTO;
  
  //Tenant Data
  tenantDataSub: Subscription;
  tenantData$: Observable<any>;

  //Subscriptions
  warehouseSub: Subscription;
  multiSubs: Subscription;

  //Wareouse List
  warehouseList$: Observable<SiteWarehouseDTO[]>;
  warehouseList: SiteWarehouseDTO[];

  //Warehouse Type list
  warehouseTypeList$: Observable<WarehouseType[]>;
  warehouseTypeList: WarehouseType[];

  //Site Products
  siteProducts$: Observable<SiteProductDTO[]>;
  siteProducts: SiteProductDTO[];
  siteProductsSub: Subscription;

  //Property List
  propertyList$: Observable<PropertyDTO[]>;
  propertyList: PropertyDTO[];

  currentWarehouse: SiteWarehouseDTO;
  isWarehouseSelected = false;

  tankStock: { value: TankStockDTO }[];

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    private translate: TranslateService,
    private dashboardService: DashboardService
  ) {
    this.tenantData$ = this.store.pipe(select(loadedTenantState));
    this.warehouseTypeList$ = this.store.pipe(select(warehouseTypesLoadedState));
    this.warehouseList$ = this.store.pipe(select(siteWarehousesLoadedState));
    this.propertyList$ = this.store.pipe(select(loadedDeviceWarehousePropertiesListSiteState));
  }

  ngOnInit(): void {
    this.loadTenantData();
    if (this.warehouseType == 'WET') {
      this.siteProducts$ = this.store.pipe(select(siteProductListLoadedState));
      this.loadSiteProducts(this._siteCode);  
    }
  }

  loadTenantData(): void {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.tenantDataFormat = data;
      }
    });
  }

  loadSiteWarehouses(code: string) {
    const stockDashboardPayload = {
      type: "PROBE_MEASUREMENTS_BY_ONE_SITE",
      range: {},
      filters: { site_number: [this.siteNumber] }
    };
    this.warehouseSub = this.dashboardService.getDashboardSalesV2(stockDashboardPayload).pipe(
      switchMap((result: any) => {
        this.tankStock = result.series;

        this.store.dispatch(getSiteWarehouseList({
          code,
          pageReq: { realSize: true, offset: 0, limit: 999 },
        }));

        return this.warehouseList$;
      }),
      catchError((err) => {
        this.store.dispatch(
          setSnackbar(
            SnackBarUtils.setSnackBar(
              this.translate.instant("TankLevel.loadFuelStockDashboardError"),
              SnackbarType.ERROR
            )
          )
        );
        return of([]);
      }),
    ).subscribe((warehouses: SiteWarehouseDTO[]) => {
      if (!warehouses || !warehouses.length) return;

      this.warehouseList = warehouses
        .filter(({ warehouseTypeCode }) => warehouseTypeCode === this.warehouseType)
        .map((warehouse) => {
          const tankStock = this.tankStock.find(
            (serie) => serie.value.warehouseCode === warehouse.code
          );
          return tankStock?.value
            ? { ...warehouse, tankStock: tankStock.value }
            : warehouse;
        });

      if (warehouses.length && this.currentWarehouse) {
        const { code: currentWarehouseCode } = this.currentWarehouse;
        this.currentWarehouse = warehouses.find(({ code }) => code === currentWarehouseCode);
      }

      this.resetWarehouse();
    });
  }

  loadWarehouseInfo(code: string) {
    this.multiSubs = zip(
      this.warehouseTypeList$.pipe(skip(1)),
      this.propertyList$.pipe(skip(1))
    ).subscribe(([warehouseTypes, properties]) => {
      if (warehouseTypes && warehouseTypes.length) {
        this.warehouseTypeList = warehouseTypes.map(
          (warehouseType: WarehouseType) => {
            return {
              code: warehouseType?.code,
              description: warehouseType?.description,
            };
          }
        );
      }

      if (properties && properties.length) {
        this.propertyList = properties;
      }
    });
    this.store.dispatch(getWarehouseTypesList(null));
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: {
          query: "is_enabled==1",
          limit: undefined,
          offset: 0,
          realSize: true,
        },
        usageCode: USAGE_CODE,
      })
    );
  }

  loadSiteProducts(code: string) {
    this.siteProductsSub = this.siteProducts$.subscribe(
      (products: SiteProductDTO[]) => {
        this.siteProducts = products;
      }
    );
    this.store.dispatch(getSiteProductList({ code }));
  }

  getChipForeColor(color: string) {
    return getColorByBgColor(`#${color}`);
  }

  public calculateDifference(
		probe: number,
		estimated: number
	): { value: number; color: string } {
		if (probe == 0 && estimated == 0)
			return { value: 0, color: "#28a745" };

		const totalLevel = probe + estimated;

		const fuelPercentage = ((probe * 100) / totalLevel) ;
		const stockPercentage = ((estimated * 100) / totalLevel) ;

		const differencePercentage = (fuelPercentage - stockPercentage);

		return {
			value: Math.abs(differencePercentage),
			color: this.generateDifferencePercentageColor(differencePercentage),
		};
	}

  private generateDifferencePercentageColor(value: number): string {
		if (value <= 5) {
			return "#28a745";
		} else if (value <= 10 && value > 5) {
			return "#ffc107";
		} else if (value > 10) {
			return "#dc3545";
		}

		return "#4f4e56";
	}

  resetWarehouse() {
    this.dialog.closeAll();
    this.isWarehouseSelected = false;
    this.currentWarehouse = null;
  }

  selectWarehouse(warehouse: SiteWarehouseDTO) {
    this.isWarehouseSelected = true;
    this.currentWarehouse = warehouse;
    this.propertyList = this.propertyList ? [...this.propertyList] : null;

    this.openWarehouseFormDialog(false);
  }

  create() {
    this.isWarehouseSelected = false;
    this.currentWarehouse = null;

    this.openWarehouseFormDialog(true);
  }
  
  openWarehouseFormDialog(isCreate: boolean) {
    this.dialog.open(WarehouseFormDialogComponent, {
      autoFocus: false,
      data: {
        isCreate,
        siteCode: this._siteCode,
        warehouseType: this.warehouseType,
        currentWarehouse: this.currentWarehouse,
        warehouseTypeList: this.warehouseTypeList,
        siteProducts: this.siteProducts,
        propertyList: this.propertyList,
      }
    });
  }

  ngOnDestroy(): void {
    if (this.tenantDataSub) {
      this.tenantDataSub.unsubscribe();
    }
    if (this.warehouseSub) {
      this.warehouseSub.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.siteProductsSub) {
      this.siteProductsSub.unsubscribe();
    }
  }
}
